import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export type ClockSample = {
  requestDate: Date;
  responseDate: Date;
  serverDate: Date;
};

export type FetchClockSample = () => Promise<ClockSample>;

export type ServerDateResult = {
  /**
   * Estimated current UTC/server date at the time this function returns.
   */
  date: Date;

  /**
   * Milliseconds to add to Date.now() to estimate server/UTC time.
   */
  offset: number;

  /**
   * Estimated uncertainty in milliseconds.
   *
   * Includes:
   * - half the request round-trip time
   * - 500ms because HTTP Date has second-level precision
   */
  uncertainty: number;

  /**
   * Request round-trip time in milliseconds for the selected sample.
   */
  roundTrip: number;

  /**
   * Returns the estimated current UTC/server date using the sampled offset.
   */
  utcNow: () => Date;
};

export type GetServerDateOptions = {
  fetchSample?: FetchClockSample;
  samples?: number;
};

export const fetchSampleImplementation: FetchClockSample = async () => {
  const requestDate = new Date();

  const response = await fetch(window.location.href, {
    cache: 'no-store',
    method: 'HEAD',
  });

  const responseDate = new Date();

  if (!response.ok) {
    throw new Error(`Bad date sample from server: ${response.statusText}`);
  }

  const dateHeader = response.headers.get('Date');

  if (dateHeader === null) {
    throw new Error('Missing Date header from server');
  }

  const serverDate = new Date(dateHeader);

  if (Number.isNaN(serverDate.getTime())) {
    throw new Error(`Invalid Date header from server: ${dateHeader}`);
  }

  return {
    requestDate,
    responseDate,
    serverDate,
  };
};

export const getServerDate = async ({
  fetchSample = fetchSampleImplementation,
  samples = 5,
}: GetServerDateOptions = {}): Promise<ServerDateResult> => {
  let best:
    | {
        offset: number;
        uncertainty: number;
        roundTrip: number;
      }
    | undefined;

  for (let index = 0; index < samples; index += 1) {
    try {
      const { requestDate, responseDate, serverDate } = await fetchSample();

      const roundTrip = responseDate.getTime() - requestDate.getTime();

      // HTTP Date has second-level precision. Adding 500ms estimates the
      // midpoint of that second.
      const estimatedServerDate = new Date(serverDate.getTime() + 500);

      const offset = estimatedServerDate.getTime() - responseDate.getTime();
      const uncertainty = roundTrip / 2 + 500;

      if (best === undefined || uncertainty < best.uncertainty) {
        best = {
          offset,
          uncertainty,
          roundTrip,
        };
      }
    } catch (error) {
      logger.error('[GET_SERVER_DATE] Failed to sample server date', { error: parseErrorMessage(error) });
    }
  }

  if (best === undefined || !best) {
    throw new Error('Unable to sample server date');
  }

  return {
    date: new Date(Date.now() + best.offset),
    offset: best.offset,
    uncertainty: best.uncertainty,
    roundTrip: best.roundTrip,
    utcNow: () => new Date(Date.now() + best!.offset),
  };
};
